import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import "../../styles/dash/PermisissionsTableModal.css";
import LoadingSpinner from '../utility/LoadingSpinner';
import { ChangePermissionsBatch } from "../../services/dashboard/ChangePermissions";

// Definir interfaces
interface RolePermission {
  idRol: number;
  roleName: string;
  statusRole: number;
  tienePermiso: number;
  action: string | null;
  actionId: number | null;
}

interface infoModule {
  action: string | null;
  actionID: number | null;
}

interface GroupedModulo {
  modulo: string;
  actions: infoModule[];
}

interface Props {
  show: boolean;
  onClose: () => void;
  moduloName: string;
  roles: RolePermission[];
  idRol: number | null;
  permisos: number[];
  modulos: GroupedModulo[];
  onBatchUpdate: (changes: { idRol: number; actionId: number | null; nuevoEstado: number }[]) => void;
}

interface ActionState {
  actionInfo: infoModule;
  hasPermission: boolean;
}

interface PendingChange {
  idRol: number;
  actionId: number | null;
  nuevoEstado: number;
}

const PermissionsTableModal: React.FC<Props> = ({
  show,
  onClose,
  moduloName,
  roles,
  idRol,
  permisos,
  modulos,
  onBatchUpdate
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [allActions, setAllActions] = useState<ActionState[]>([]);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);

  // Inicializar estado con las acciones y permisos
  useEffect(() => {
    if (moduloName && show) {
      setIsLoading(true);
      const selectedModule = modulos.find(m => m.modulo === moduloName);
      
      if (selectedModule) {
        // Filtrar acciones con actionID nulo
        const validActions = selectedModule.actions.filter(action => 
          action.actionID !== null
        );
        
        const actionsWithStatus = validActions.map(action => {
          const permission = roles.find(r => 
            r.actionId === action.actionID && 
            r.idRol === idRol
          );
          
          return {
            actionInfo: action,
            hasPermission: permission ? permission.tienePermiso === 1 : false
          };
        });
        
        setAllActions(actionsWithStatus);
      }
      
      setPendingChanges([]);
      setIsLoading(false);
    }
  }, [moduloName, roles, modulos, idRol, show]);

  const handleChange = useCallback((actionInfo: infoModule, newPermission: boolean) => {
    if (actionInfo.actionID === null) {
      console.warn("Intento de cambiar permiso para acción con ID nulo");
      return;
    }
    
    setAllActions(prev =>
      prev.map(item =>
        item.actionInfo.actionID === actionInfo.actionID
          ? { ...item, hasPermission: newPermission }
          : item
      )
    );

    setPendingChanges(prev => {
      const existingIndex = prev.findIndex(
        change => change.actionId === actionInfo.actionID
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          nuevoEstado: newPermission ? 1 : 0
        };
        return updated;
      } else {
        return [
          ...prev,
          {
            idRol: idRol!,
            actionId: actionInfo.actionID,
            nuevoEstado: newPermission ? 1 : 0
          }
        ];
      }
    });
  }, [idRol]);

  const handleSaveChanges = async () => {
    if (idRol === null || pendingChanges.length === 0) return;

    try {
      setIsLoading(true);
      
      const validChanges = pendingChanges.filter(change => 
        change.actionId !== null
      ) as {
        idRol: number;
        actionId: number;
        nuevoEstado: number;
      }[];

      const response = await ChangePermissionsBatch(idRol, validChanges);

      if (response.status === 200) {
        onBatchUpdate(validChanges);
        setPendingChanges([]);
      }
    } catch (err) {
      console.error("Error saving changes:", err);
      
      setAllActions(prevActions => 
        prevActions.map(actionItem => {
          const originalPermission = roles.find(r => 
            r.actionId === actionItem.actionInfo.actionID && 
            r.idRol === idRol
          );
          return {
            ...actionItem,
            hasPermission: originalPermission ? originalPermission.tienePermiso === 1 : false
          };
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      contentClassName="chatbot-form-modal"
    >
      <Modal.Header closeButton closeVariant="white" className="border-0" />
      <Modal.Body className="chatbot-form-content">
        <h5 className="form-title text-center mb-4">Permisos para módulo: {moduloName}</h5>

        {isLoading ? (
          <div className="loading-spinner-container">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="table-container">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Acción</th>
                  <th>Permiso</th>
                </tr>
              </thead>
              <tbody>
                {allActions.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.actionInfo.action || "Sin acción definida"}</td>
                    <td>
                      <label className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={item.hasPermission}
                          onChange={(e) => handleChange(item.actionInfo, e.target.checked)}
                          disabled={!permisos.includes(20)}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="modal-footer">
          <button 
            className="save-button" 
            onClick={handleSaveChanges} 
            disabled={pendingChanges.length === 0 || isLoading}
          >
            {isLoading ? "Guardando..." : "Guardar cambios"}
          </button>

          <button 
            className="close-button" 
            onClick={onClose} 
            disabled={isLoading}
          >
            Cerrar
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PermissionsTableModal;